import { useEffect, useMemo, useState } from "react"

import { Modal as AntModal, App, Divider, Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import InviteForm from "../../../../Components/Forms/InviteForm"
import Modal from "../../../../Components/Modal"
import Spinner from "../../../../Components/Spinner"
import { RoutesPaths } from "../../../../constants/user-routes"
import Icon from "../../../../Icon"
import { getUsersManagementRoles } from "../../../Setting/redux/setting.thunks"
import {
  getDisputesStatistics,
  getReasonCategories,
  getTeamMembers
} from "../../redux/disputes.thunks"
import { REASON_CATEGORY_FALLBACK } from "../AllPage/constants/reasonCategories"
import {
  deactivateRecover,
  getProfile,
  savePreferences
} from "../ConfigurePage/redux/configure.thunks"
import ActivationBanner from "./components/ActivationBanner/ActivationBanner"
import AssignmentRuleModal from "./components/AssignmentRuleModal"
import AssignmentRules from "./components/AssignmentRules/AssignmentRules"
import AutoApproveSection from "./components/AutoApproveSection/AutoApproveSection"
import AutoSubmitModal from "./components/AutoSubmitModal/AutoSubmitModal"
import CategoryExceptionModal from "./components/CategoryExceptionModal"
import CategoryRules from "./components/CategoryRules/CategoryRules"
import DangerZone from "./components/DangerZone/DangerZone"
import EditNumberForm from "./components/EditNumberForm/EditNumberForm"
import LowValueSection from "./components/LowValueSection"
import RepeatDisputerSection from "./components/RepeatDisputerSection"
import { DEFAULTS, EMPTY } from "./constants/defaults"
import { MODAL_DESCRIPTIONS, MODAL_TITLES } from "./constants/modalCopy"

import styles from "./preferences.module.less"

const { Text } = Typography

const PreferencesPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { notification, modal } = App.useApp()
  const dispatch = useDispatch()

  const preferences = useSelector((s) => s.disputesConfigure.profile.preferences)
  const fetching = useSelector((s) => s.disputesConfigure.profile.fetching)
  const isActivated = useSelector((s) => s.disputesConfigure.profile.isActivated)
  const buildingBlocks = useSelector((s) => s.disputesConfigure.profile.buildingBlocks)
  const setup = useSelector((s) => s.disputesConfigure.profile.setup)
  const statistics = useSelector((s) => s.disputes.list.statistics)
  const teamMembers = useSelector((s) => s.disputes.team.members)
  const isMobile = useSelector((s) => s.nav.isMobile)
  // The `?? EMPTY` sits outside the selector on purpose: returning a fresh [] from
  // inside one makes every unrelated store update look like a change and re-render.
  const reasonCategoryOptions = useSelector((s) => s.disputes.reasonCategories?.options) ?? EMPTY
  const businessAccounts = useSelector((s) => s.user?.businessAccounts) ?? EMPTY
  const businessAccountsWithRole = useSelector((s) => s.user?.businessAccountsWithRole) ?? EMPTY
  const roles = useSelector((s) => s.setting?.roles) ?? EMPTY

  const connectedProcessors = buildingBlocks?.processors?.connected ?? EMPTY
  // Which processors have finished setup, per the account rather than this browser.
  const finishedProcessors = useMemo(
    () =>
      Object.entries(setup || {})
        .filter(([, entry]) => entry?.setup_finished)
        .map(([slug]) => slug),
    [setup]
  )

  // Stripe bills $15 per evidence submission (and another $15 on a loss), so ignoring
  // low-value disputes is especially worthwhile for Stripe merchants. Derived from the
  // CONNECTED processors (profile building blocks) rather than the dispute statistics,
  // so it holds even before any disputes have synced (statistics.processors is empty
  // until then). Declared here because the low-value DEFAULT depends on it.
  const isStripeUser =
    connectedProcessors.includes("stripe") || (statistics?.processors || []).includes("stripe")
  // Ignore low-value disputes defaults ON at $15 for a Stripe merchant and OFF for
  // everyone else. It is only a DEFAULT: the merchant's own choice is saved explicitly,
  // so a Stripe merchant can still turn it off and it stays off.
  const merged = useMemo(
    () => ({ ...DEFAULTS, ignore_low_value_enabled: isStripeUser, ...(preferences || {}) }),
    [preferences, isStripeUser]
  )
  const [editKey, setEditKey] = useState(null)
  const [autoSubmitOpen, setAutoSubmitOpen] = useState(false)
  // Post-activation: any Preferences edit flags pending changes; the banner's
  // "Confirm changes" button acknowledges them (changes only affect the NEXT
  // disputes Recover receives, never those already in the queue).
  const [pendingChanges, setPendingChanges] = useState(false)
  // Add/edit Category Exception modal (per-category automatic-approval rules).
  const [excOpen, setExcOpen] = useState(false)
  const [excInitial, setExcInitial] = useState(null)
  // Add/edit assignment-rule modal + invite-user modal, and locally-invited members
  // (playground: an invite records the teammate here so they show in the pickers).
  const [asgOpen, setAsgOpen] = useState(false)
  const [asgInitial, setAsgInitial] = useState(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [invited, setInvited] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("chargeback.playground.invitedMembers")) || []
    } catch {
      return []
    }
  })
  // Invited team members for the auto-assign picker (disputes-slice fetch).
  const members = useMemo(
    () => (Array.isArray(teamMembers) ? teamMembers : teamMembers?.results || []),
    [teamMembers]
  )
  // Real team members plus any invited this session (playground), so invitees are
  // pickable in the assignment controls.
  const allMembers = useMemo(() => [...members, ...invited], [members, invited])
  // Invite modal data: the SAME sources the Team page (PersonalSettings/Users) feeds
  // InviteForm, so the popup is identical. The playground mock lacks can_invite, so
  // that part of the Team page's owner/admin filter is skipped; fall back to the
  // plain accounts list if the role-annotated one hasn't loaded yet.
  const inviteAccountList = useMemo(() => {
    const withRole = (businessAccountsWithRole || []).filter(
      (ba) => ba?.role?.id <= 2 && !ba?.is_demo
    )
    return withRole.length ? withRole : businessAccounts || []
  }, [businessAccountsWithRole, businessAccounts])

  // Category dropdown options: use the API-hydrated list when available,
  // fallback otherwise. Same shape ({value, label}) either way.
  const categoryOptions = useMemo(
    () =>
      reasonCategoryOptions && reasonCategoryOptions.length
        ? reasonCategoryOptions
        : REASON_CATEGORY_FALLBACK,
    [reasonCategoryOptions]
  )

  useEffect(() => {
    document.title = "Recover Preferences - Chargeback"
    if (!preferences || Object.keys(preferences).length === 0) dispatch(getProfile())
    if (!reasonCategoryOptions.length) dispatch(getReasonCategories())
    if (!members.length) dispatch(getTeamMembers())
    // Roles power the Invite user form's permission selector (same as the Team page).
    if (!roles.length) dispatch(getUsersManagementRoles())
    // Counts power the deactivate warning (how many cases are still in flight).
    dispatch(getDisputesStatistics())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The confirmation waits for the server to accept the change — announcing it on
  // dispatch would report a save that never reached the backend. A rejection is already
  // notified by the thunk, and the slice rolls the optimistic value back.
  const patch = async (next, fieldLabel) => {
    setPendingChanges(true)
    try {
      await dispatch(savePreferences({ patch: next })).unwrap()
      if (fieldLabel) {
        notification.success({ message: t("_field_ updated", { field: fieldLabel }) })
      }
    } catch {
      /* error handled in thunk */
    }
  }

  // Every confirm on this page goes through here. Standard antd confirm buttons
  // (native colors, fonts and frames), with two scoped corrections via the class:
  // buttons sit compact on the right on desktop instead of stretching across the
  // modal, and at the same 40px as the other pop-ups' size-large controls. Also
  // mobile-safe: centered collides with the bottom-sheet CSS and pushed the dialog
  // off-screen, which read as "double pop-ups" when a second tap stacked an
  // invisible duplicate.
  const confirmSheet = ({ okText, okDanger, onOk, ...rest }) =>
    modal.confirm({
      ...rest,
      centered: !isMobile,
      className: styles.confirm,
      okText,
      cancelText: t("Cancel"),
      okButtonProps: { danger: !!okDanger, size: "large" },
      cancelButtonProps: { size: "large" },
      onOk
    })

  const handleDeactivate = () => {
    // "Finish in-progress, stop new": deactivation halts all NEW automation while
    // cases already submitted keep running to a verdict (and the success fee still
    // applies to wins). Surface the exact in-flight count so nothing surprises later.
    const pending = Number(statistics?.pending_decision || 0)
    confirmSheet({
      title: t("Deactivate Recover?"),
      okText: t("Deactivate"),
      okDanger: true,
      content: (
        <Flex vertical gap={8}>
          <Text>
            {t(
              "Recover immediately stops preparing evidence and auto-submitting disputes. You can reactivate any time."
            )}
          </Text>
          {pending > 0 ? (
            <Text type="secondary">
              {pending === 1
                ? t(
                    "1 case already submitted to issuers will continue to a decision, and the 25% success fee still applies if we win."
                  )
                : t(
                    "_n_ cases already submitted to issuers will continue to a decision, and the 25% success fee still applies to any we win.",
                    { n: pending }
                  )}
            </Text>
          ) : null}
        </Flex>
      ),
      onOk: () => {
        // The deactivateRecover thunk clears the per-account setup-finished marker on
        // confirmed success (so Configure reopens as the wizard); isActivated flips
        // false and the shell shows the activation screen.
        dispatch(deactivateRecover())
        navigate(RoutesPaths.recover.all)
      }
    })
  }

  // Auto-submission exceptions: merchant-defined carve-outs (conditions on
  // category/amount) that hold matching disputes back from auto-submission. Persisted in
  // preferences.approval_rules (storage key unchanged so saved data still loads) and
  // rendered by CategoryRules. Every add/edit/remove is confirmed and reported as
  // applying to the next dispute received (not disputes already in the queue).
  const approvalRules = merged.approval_rules || []
  const openRuleAdd = () => {
    setExcInitial(null)
    setExcOpen(true)
  }
  const openRuleEdit = (id) => {
    setExcInitial(approvalRules.find((r) => r.id === id) || null)
    setExcOpen(true)
  }
  const applyRules = (next, savedMsg) => {
    dispatch(savePreferences({ patch: { approval_rules: next } }))
    setPendingChanges(true)
    notification.success({
      message: savedMsg,
      description: t(
        "It applies to the next dispute Recover receives, not disputes already in your queue."
      )
    })
  }
  const saveRule = (rule) => {
    const withId = rule.id ? rule : { ...rule, id: `rule-${Date.now()}` }
    confirmSheet({
      title: rule.id ? t("Update this exception?") : t("Add this exception?"),
      content: t(
        "Recover applies changes to the next dispute it receives, not to disputes already in your queue."
      ),
      okText: rule.id ? t("Update exception") : t("Add exception"),
      onOk: () => {
        const next = [...approvalRules.filter((r) => r.id !== withId.id), withId]
        applyRules(next, rule.id ? t("Exception updated") : t("Exception added"))
        setExcOpen(false)
      }
    })
  }
  const removeRule = (id) => {
    confirmSheet({
      title: t("Remove this exception?"),
      okDanger: true,
      okText: t("Remove"),
      content: t(
        "Disputes that matched it will go back to being auto-submitted, starting with the next dispute Recover receives."
      ),
      onOk: () =>
        applyRules(
          approvalRules.filter((r) => r.id !== id),
          t("Exception removed")
        )
    })
  }

  // Assignment rules: route matching disputes to a specific team member. Same
  // confirm + "applies to the next dispute" contract as the approval rules.
  const assignmentRules = merged.assignment_rules || []
  const openAsgAdd = () => {
    setAsgInitial(null)
    setAsgOpen(true)
  }
  const openAsgEdit = (id) => {
    setAsgInitial(assignmentRules.find((r) => r.id === id) || null)
    setAsgOpen(true)
  }
  const applyAssignmentRules = (next, savedMsg) => {
    dispatch(savePreferences({ patch: { assignment_rules: next } }))
    setPendingChanges(true)
    notification.success({
      message: savedMsg,
      description: t(
        "It applies to the next dispute Recover receives, not disputes already in your queue."
      )
    })
  }
  const saveAssignmentRule = (rule) => {
    const withId = rule.id ? rule : { ...rule, id: `asg-${Date.now()}` }
    confirmSheet({
      title: rule.id ? t("Update this rule?") : t("Add this rule?"),
      content: t(
        "Recover applies rule changes to the next dispute it receives, not to disputes already in your queue."
      ),
      okText: rule.id ? t("Update rule") : t("Add rule"),
      onOk: () => {
        const next = [...assignmentRules.filter((r) => r.id !== withId.id), withId]
        applyAssignmentRules(next, rule.id ? t("Rule updated") : t("Rule added"))
        setAsgOpen(false)
      }
    })
  }
  const removeAssignmentRule = (id) => {
    confirmSheet({
      title: t("Remove this rule?"),
      okDanger: true,
      okText: t("Remove"),
      content: t("Recover will stop applying it, starting with the next dispute it receives."),
      onOk: () =>
        applyAssignmentRules(
          assignmentRules.filter((r) => r.id !== id),
          t("Rule removed")
        )
    })
  }
  // Invite a teammate (playground): record them locally so they show in the pickers.
  // Receives InviteForm's values ({ name, email, role_id, business_accounts_ids }).
  const handleInvite = ({ name, email }) => {
    const next = [...invited, { id: `invited-${Date.now()}`, name, email }]
    setInvited(next)
    try {
      localStorage.setItem("chargeback.playground.invitedMembers", JSON.stringify(next))
    } catch {
      /* ignore */
    }
    notification.success({
      message: t("Invitation sent"),
      description: t("We emailed _email_ an invite. They can be assigned disputes right away.", {
        email
      })
    })
    setInviteOpen(false)
  }

  const ignoreLowOn = !!merged.ignore_low_value_enabled
  // Preferences unlock once Recover is active OR once Configure is finished but not yet
  // activated: setting preferences is now a required step BEFORE activation (the merchant
  // reviews and tunes them, then activates from here in flow v2 or from the summary in
  // v1). ONE finished integration is enough: the merchant may activate with the first
  // processor and set up the second later. Only a merchant who hasn't finished any
  // setup at all (and isn't active) sees the read-only defaults.
  const setupFinished = finishedProcessors.length > 0
  const pendingActivation = setupFinished && !isActivated
  const locked = !isActivated && !setupFinished
  // Auto-submission: evidence is always submitted automatically (no on/off toggle).
  // The page shows a read-only summary; the mode/threshold/wait-days are edited in the
  // Auto-submission setup modal (which also absorbed the old Submission-delay section).
  const autoSubmitMode = merged.auto_submit_mode === "threshold" ? "threshold" : "all"
  const autoSubmitAmount = Number(merged.max_auto_approve_amount || 0)
  const autoSubmitDelay = Number(merged.auto_submit_delay_days ?? 2)
  // Shown inside a $-prefixed field (same look as "Ignore disputes below"), so the
  // threshold summary carries no inline dollar sign.
  const autoSubmitSummary =
    autoSubmitMode === "threshold"
      ? t("Auto-submitted up to _amount_", { amount: autoSubmitAmount.toFixed(2) })
      : t("All disputes are auto-submitted")
  const onSaveAutoSubmit = ({ mode, amount, delayDays }) => {
    patch(
      {
        auto_submit_mode: mode,
        max_auto_approve_amount: Number(amount) || 0,
        auto_submit_delay_days: Number(delayDays) || 0
      },
      t("Auto-submission")
    )
    setAutoSubmitOpen(false)
  }

  const onToggleIgnoreLowValue = (checked) =>
    patch({ ignore_low_value_enabled: checked }, t("Ignore low-value disputes"))

  // Repeat-disputer routing is off by default; the toggle gates it and the threshold is
  // only editable while it's on. Turning it on with no threshold yet seeds a sensible
  // starting value so the field never reads a meaningless "0".
  const repeatOn = !!merged.repeat_disputer_enabled
  const onToggleRepeatDisputer = (checked) => {
    const next = { repeat_disputer_enabled: checked }
    if (checked && Number(merged.repeat_disputer_threshold || 0) < 1)
      next.repeat_disputer_threshold = 2
    patch(next, t("Repeat disputers"))
  }

  const onEditNumberSubmit = (val) => {
    const k = editKey
    if (!k) return
    patch({ [k]: val }, t(MODAL_TITLES[k]))
    setEditKey(null)
  }

  // While the profile is still loading, show a spinner rather than painting the
  // DEFAULTS as if they were the merchant's saved settings.
  if (fetching && (!preferences || Object.keys(preferences).length === 0)) {
    return (
      <div className={styles.page}>
        <Spinner />
      </div>
    )
  }

  // When Configure is finished but Recover isn't on yet, Preferences is the required
  // pre-activation step, and activation itself happens on the setup summary — here we
  // only point the merchant back to it. Outside that pending window the usual
  // self-gated Configure nudge applies.
  // Acknowledges edits made after activation. Playground behavior: the settings are
  // already saved per-edit (see patch()); Confirm just closes the loop and reminds
  // when the changes take effect.
  const handleConfirmChanges = () => {
    setPendingChanges(false)
    notification.success({
      message: t("Changes confirmed"),
      description: t(
        "They apply to the next disputes you receive, not to disputes already in your queue."
      )
    })
  }

  return (
    <div className={styles.page}>
      <ActivationBanner
        pendingActivation={pendingActivation}
        isActivated={isActivated}
        pendingChanges={pendingChanges}
        onConfirmChanges={handleConfirmChanges}
        onBackToSummary={() => navigate(RoutesPaths.recover.configure_section("summary"))}
      />

      <AutoApproveSection
        locked={locked}
        summary={autoSubmitSummary}
        delayDays={autoSubmitDelay}
        onEdit={() => setAutoSubmitOpen(true)}
      />
      <Divider />

      {/* Automatic approval rules: custom auto-submit / skip rules for specific cases
          (category / amount / card network / issuer). Empty by default; the add/edit
          modal is rendered below. */}
      <CategoryRules
        rules={approvalRules}
        locked={locked}
        onAdd={openRuleAdd}
        onEdit={openRuleEdit}
        onRemove={removeRule}
      />
      <Divider />

      <LowValueSection
        locked={locked}
        fetching={fetching}
        isStripeUser={isStripeUser}
        enabled={ignoreLowOn}
        amount={merged.ignore_below_amount}
        onToggle={onToggleIgnoreLowValue}
        onEdit={() => setEditKey("ignore_below_amount")}
      />
      <Divider />

      {/* Assignment: one unified block. Default (round-robin) assignees for anything
          not matched, then per-condition rules for specific cases, plus Invite user. */}
      <AssignmentRules
        rules={assignmentRules}
        members={allMembers}
        defaultAssignees={merged.auto_assign_to || []}
        onChangeDefault={(ids) => patch({ auto_assign_to: ids }, t("Auto-assign"))}
        onInvite={() => setInviteOpen(true)}
        locked={locked}
        onAdd={openAsgAdd}
        onEdit={openAsgEdit}
        onRemove={removeAssignmentRule}
      />
      <Divider />

      <RepeatDisputerSection
        locked={locked}
        fetching={fetching}
        enabled={repeatOn}
        threshold={merged.repeat_disputer_threshold}
        onToggle={onToggleRepeatDisputer}
        onEdit={() => setEditKey("repeat_disputer_threshold")}
      />

      {/* Edit modal */}
      <Modal
        visible={!!editKey}
        isMobile={isMobile}
        handleClose={() => setEditKey(null)}
        title={editKey ? t(MODAL_TITLES[editKey]) : ""}
        footer={null}
        destroyOnClose
        width={482}
        closeIcon={<Icon type="cross_small" />}
        classNames={{ content: "clear-bg" }}
        className="change-refund-form-modal"
      >
        <Text type="secondary">{editKey ? t(MODAL_DESCRIPTIONS[editKey]) : ""}</Text>
        <EditNumberForm
          fieldKey={editKey}
          initial={editKey ? Number(merged[editKey] || 0) : 0}
          onSubmit={onEditNumberSubmit}
          onClose={() => setEditKey(null)}
        />
      </Modal>

      {/* Auto-submission setup (mode + threshold + wait-days). */}
      <AutoSubmitModal
        visible={autoSubmitOpen}
        onClose={() => setAutoSubmitOpen(false)}
        onSave={onSaveAutoSubmit}
        initial={{ mode: autoSubmitMode, amount: autoSubmitAmount, delayDays: autoSubmitDelay }}
      />

      {/* Add / edit an auto-submission exception. `otherRules` excludes the one being
          edited, so the modal can check the candidate against the rest of the list
          (duplicate / already-covered) without flagging a rule against itself. */}
      <CategoryExceptionModal
        visible={excOpen}
        onClose={() => setExcOpen(false)}
        onSave={saveRule}
        initial={excInitial}
        categoryOptions={categoryOptions}
        otherRules={approvalRules.filter((r) => r.id !== excInitial?.id)}
      />

      {/* Add / edit an assignment rule. `otherRules` excludes the one being edited so a
          rule is never flagged as overlapping itself. */}
      <AssignmentRuleModal
        visible={asgOpen}
        onClose={() => setAsgOpen(false)}
        onSave={saveAssignmentRule}
        initial={asgInitial}
        categoryOptions={categoryOptions}
        members={allMembers}
        otherRules={assignmentRules.filter((r) => r.id !== asgInitial?.id)}
      />

      {/* Invite a teammate: the EXACT modal + form the Team page uses
          (PersonalSettings/Users), so the two invite popups stay identical. */}
      <AntModal
        open={inviteOpen}
        centered={!isMobile}
        width={442}
        title={t("Invite user")}
        footer={null}
        closable="true"
        onCancel={() => setInviteOpen(false)}
        destroyOnClose
        classNames={{ content: "clear-bg" }}
      >
        <InviteForm
          businessAccountList={inviteAccountList}
          roles={roles}
          initialValues={null}
          isBusinessSettingsPage={false}
          onSubmit={handleInvite}
          onClose={() => setInviteOpen(false)}
        />
      </AntModal>

      {/* Danger zone */}
      <Divider />
      <DangerZone fetching={fetching} isActivated={isActivated} onDeactivate={handleDeactivate} />
    </div>
  )
}

export default PreferencesPage
